import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Gift, LogOut, Shield } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { HouseholdCtx } from "@/components/AppShell";
import { api, errMsg, download } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PLANS } from "@/lib/constants";
import { pushSupported, currentSubscription, enablePush, disablePush, isIos, isStandalone } from "@/lib/push";

const Section = ({ title, id, children }) => (
  <section id={id} className="pl-card p-5 space-y-3"><h2 className="font-heading text-xl font-bold">{title}</h2>{children}</section>
);

function HouseholdForm({ h, reload }) {
  const keys = ["name", "vet_name", "vet_clinic", "vet_phone", "vet_address", "emergency_name", "emergency_phone", "house_notes"];
  const [f, setF] = useState(() => Object.fromEntries(keys.map((k) => [k, h[k] || ""])));
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/household", { ...f, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }); toast.success("Saved"); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const inp = (k, label, type = "text") => <div><label className="pl-label">{label}</label><input type={type} className="pl-input" data-testid={`household-${k}-input`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /></div>;
  return (
    <form onSubmit={save} className="space-y-3">
      {inp("name", "Household name")}
      <div className="grid sm:grid-cols-2 gap-3">{inp("vet_name", "Vet name")}{inp("vet_clinic", "Clinic")}{inp("vet_phone", "Vet phone", "tel")}{inp("vet_address", "Clinic address")}
        {inp("emergency_name", "Emergency contact")}{inp("emergency_phone", "Emergency phone", "tel")}</div>
      <div><label className="pl-label">House notes for sitters</label><textarea rows={3} className="pl-input py-3" data-testid="household-house_notes-input" value={f.house_notes} onChange={(e) => setF({ ...f, house_notes: e.target.value })} placeholder="Wi-Fi, where food is stored, door codes..." /></div>
      <button className="pl-btn-primary w-full" data-testid="household-save-btn">Save household</button>
    </form>
  );
}

function Billing() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/billing").then((r) => setB(r.data));
  useEffect(() => { load(); }, []);
  const checkout = async (plan) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  const act = async (path) => { try { await api.post(path); toast.success("Updated"); load(); } catch (err) { toast.error(errMsg(err)); } };
  if (!b) return null;
  const subscribed = ["active", "trialing"].includes(b.subscription_status);
  const refLink = `${window.location.origin}/?ref=${b.referral_code}`;
  return (
    <>
      <Section title="Plan & billing" id="billing">
        <p data-testid="billing-status" className="text-sm">
          {subscribed ? <>Subscribed · <b className="capitalize">{b.plan}</b>{b.subscription_status === "trialing" ? " (first charge when your trial ends)" : ""}{b.cancel_at_period_end ? " · cancels at period end" : ""}</>
            : b.has_access ? <>Free trial · <b>{b.trial_days_left} days left</b> (ends {new Date(b.trial_ends_at).toLocaleDateString()})</> : <b className="text-[#C2410C]">Trial ended. Subscribe to keep logging.</b>}
        </p>
        {!subscribed && (
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(PLANS).map(([k, p]) => (
              <button key={k} disabled={busy} onClick={() => checkout(k)} data-testid={`billing-subscribe-${k}-btn`} className={`rounded-2xl border-2 p-4 text-left ${k === "yearly" ? "border-[#F59E0B] bg-[#FFFBF0]" : "border-[#EFECE6]"}`}>
                <p className="font-heading text-2xl font-extrabold">{p.price}<span className="text-sm font-semibold text-slate-500">/{p.per}</span></p>
                <p className="text-xs text-slate-500 capitalize">{k}{p.note ? ` · ${p.note}` : ""}</p>
              </button>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-500">One price per household. Unlimited pets, caregivers and sitter links. Payments are handled securely by Stripe.</p>
        {subscribed && !b.cancel_at_period_end && <button onClick={() => act("/billing/cancel")} className="text-sm text-slate-500 font-semibold" data-testid="billing-cancel-btn">Cancel subscription</button>}
        {subscribed && b.cancel_at_period_end && <button onClick={() => act("/billing/resume")} className="text-sm text-[#D97706] font-semibold" data-testid="billing-resume-btn">Keep my subscription</button>}
      </Section>
      <Section title="Give a month, get a month">
        <p className="text-sm text-slate-600 flex gap-2"><Gift className="h-5 w-5 text-[#D97706] shrink-0" />Get one free month for every household you refer that subscribes. {b.referrals > 0 && <b>{b.referrals} so far!</b>}</p>
        <div className="flex gap-2"><input readOnly className="pl-input text-sm" value={refLink} data-testid="referral-link-input" />
          <button className="pl-btn-ghost shrink-0" data-testid="referral-copy-btn" onClick={() => { navigator.clipboard.writeText(refLink); toast.success("Copied"); }}>Copy</button></div>
      </Section>
    </>
  );
}

function PushToggle() {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { currentSubscription().then((s) => setOn(!!s)).catch(() => {}); }, []);
  if (!pushSupported()) {
    return <p className="text-sm text-slate-500" data-testid="push-unsupported">{isIos() && !isStandalone() ? "On iPhone: tap Share → Add to Home Screen, open PackLog from your home screen, then turn on lock-screen alerts here." : "This browser doesn't support lock-screen notifications."}</p>;
  }
  const toggle = async (v) => {
    setBusy(true);
    try {
      if (v) { await enablePush(); toast.success("Lock-screen alerts are on for this device"); } else { await disablePush(); toast("Lock-screen alerts off for this device"); }
      setOn(v);
    } catch (err) { toast.error(err.message || errMsg(err)); }
    setBusy(false);
  };
  return (
    <label className="flex items-center justify-between gap-3"><span className="text-sm"><b>Lock-screen alerts on this device</b><br /><span className="text-slate-500">Medication due and not-logged alerts, reminders and sitter updates, even when PackLog is closed.</span></span>
      <Switch checked={on} disabled={busy} onCheckedChange={toggle} data-testid="push-notifications-switch" /></label>
  );
}

function Notifications({ user, refresh }) {
  const toggleEmail = async (v) => { await api.put("/me/prefs", { email_reminders: v }); refresh(); };
  return (
    <Section title="Reminders">
      <PushToggle />
      <label className="flex items-center justify-between gap-3"><span className="text-sm"><b>Email reminders</b><br /><span className="text-slate-500">Upcoming vaccines and preventives, and missed-medication alerts.</span></span>
        <Switch checked={user.email_reminders !== false} onCheckedChange={toggleEmail} data-testid="email-reminders-switch" /></label>
      <p className="text-xs text-slate-500">Tip: add PackLog to your home screen (Share → Add to Home Screen) for one-tap access. Turn alerts on separately on each phone.</p>
    </Section>
  );
}

function DataSection({ isOwner }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [confirm, setConfirm] = useState("");
  const del = async () => {
    try { await api.delete("/account"); await logout(); toast.success("Your account and data were deleted."); nav("/"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Section title="Your data">
      <p className="text-sm text-slate-600 flex gap-2"><Shield className="h-5 w-5 text-[#10B981] shrink-0" />We never sell or share your data. Backed up automatically every day.</p>
      <div className="grid grid-cols-2 gap-3">
        <button className="pl-btn-ghost" data-testid="export-pdf-btn" onClick={() => download("/export/pdf", "packlog-export.pdf").catch(() => toast.error("Export failed"))}><Download className="h-4 w-4" />PDF</button>
        <button className="pl-btn-ghost" data-testid="export-csv-btn" onClick={() => download("/export/csv", "packlog-export.zip").catch(() => toast.error("Export failed"))}><Download className="h-4 w-4" />CSV</button>
      </div>
      <AlertDialog>
        <AlertDialogTrigger asChild><button className="text-sm text-[#C2410C] font-semibold" data-testid="delete-account-btn">Delete account and all data</button></AlertDialogTrigger>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader><AlertDialogTitle>Delete everything?</AlertDialogTitle>
            <AlertDialogDescription>{isOwner ? "This permanently deletes your account, your household, every pet, log, record and sitter link, and cancels your subscription. Other members lose access." : "This permanently deletes your account. The household's pets and logs stay with the owner."} Type DELETE to confirm.</AlertDialogDescription></AlertDialogHeader>
          <input className="pl-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="delete-confirm-input" />
          <AlertDialogFooter><AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={confirm !== "DELETE"} onClick={del} className="bg-[#E07A5F]" data-testid="delete-confirm-btn">Delete forever</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Section>
  );
}

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const { household, reload } = useContext(HouseholdCtx);
  const nav = useNavigate();
  useEffect(() => { if (window.location.hash === "#billing") setTimeout(() => document.getElementById("billing")?.scrollIntoView({ behavior: "smooth" }), 300); }, [household]);
  if (!household) return <div className="pl-card h-60 animate-pulse" />;
  return (
    <div className="space-y-5" data-testid="settings-page">
      <h1 className="font-heading text-4xl font-extrabold">Settings</h1>
      <Billing />
      <Section title="Household, vet & emergency"><HouseholdForm h={household} reload={reload} /></Section>
      <Notifications user={user} refresh={refresh} />
      <DataSection isOwner={household.is_owner} />
      <div className="flex flex-wrap gap-4 text-sm text-slate-500 px-1">
        <span>{user.email}</span>
        <Link to="/privacy" data-testid="settings-privacy-link">Privacy</Link><Link to="/terms" data-testid="settings-terms-link">Terms</Link>
        {user.is_admin && <Link to="/admin" className="font-semibold text-[#D97706]" data-testid="settings-admin-link">Admin metrics</Link>}
      </div>
      <button onClick={async () => { await logout(); nav("/"); }} className="pl-btn-ghost w-full" data-testid="logout-btn"><LogOut className="h-4 w-4" />Log out</button>
    </div>
  );
}
